import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  deleteIssueFile,
  downloadFile,
  getIssueFiles,
  saveIssueFile,
  uploadFile,
} from '../../../../../packages/common/api';
import AttachIcon from '../../../../../packages/common/shared-ui/src/icons/AttachIcon';
import DeleteFileIcon from '../../../../../packages/common/shared-ui/src/icons/DeleteFileIcon';
import DownloadFileIcon from '../../../../../packages/common/shared-ui/src/icons/DownloadFileIcon';
import PlusIcon from '../../../../../packages/common/shared-ui/src/icons/PlusIcon';
import { useTranslation } from '../../../../../packages/common/utils/getLocalString';
import { generateRedirectUrl } from '../../../../../packages/common/utils/getRedirectUrl';

// Original class: addButton
const StyledAddButton = styled(IconButton)(theme => ({
  '& .MuiSvgIcon-root': {
    '& path': {
      transition: '.2s ease',
    },
  },

  '&:hover': {
    '& .MuiSvgIcon-root': {
      '& path': {
        fill: '#7070B8',
      },
    },
  },
}));
// Original class: issueLink
const StyledIssueLink = styled(Box)(theme => ({
  '&:hover': {
    textDecoration: 'underline',
  },
}));
// Original class: hoveredIcon
const StyledHoveredIcon = styled(IconButton)(theme => ({
  '& .MuiSvgIcon-root': {
    '& path': {
      transition: '.2s ease',
    },
  },

  '&:hover': {
    '& .MuiSvgIcon-root': {
      '& path': {
        fill: '#7174AC',
      },
    },
  },
}));

const saveAs = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();

  // Очистка
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const IssueFiles = ({ permissions, obtainFiles, viewOnly = false }) => {
  const getLocalString = useTranslation();
  const params = useParams();
  const redmineToken = useSelector(state => state.user.redmine_access_token);
  const [loading, setLoading] = useState(true);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    let ignore = false;
    async function getFiles() {
      const { data: files } = await getIssueFiles(params.id);
      if (!ignore) {
        return files;
      }
    }
    getFiles()
      .then(res => {
        setFiles(res);
        !viewOnly && obtainFiles(res);
        setLoading(false);
      })
      .catch(error => console.error(error, 'ERROR IN GET'));
    return () => {
      ignore = true;
    };
  }, [params.id]);

  const handleFileUpload = useCallback(
    async event => {
      setLoadingUpload(true);
      const { data: fileData } = await uploadFile(
        event.target.files[0],
        event.target.files[0].name,
      );
      if (fileData.upload.token) {
        const data = {
          token: fileData.upload.token,
          fileName: event.target.files[0].name,
          contentType: event.target.files[0].type,
        };
        try {
          const { data: response } = await saveIssueFile(params.id, data);
          if (response) {
            setFiles(response);
            setLoadingUpload(false);
          }
        } catch (error) {
          console.error('ERROR WITH SAVE', error);
        }
      }
    },
    [params.id],
  );

  const handleDeleteIssueFile = useCallback(
    async (id, index) => {
      try {
        const response = await deleteIssueFile(params.id, id);
        if (response) {
          const newArray = files.filter((file, i) => i !== index);
          setFiles(newArray);
        }
      } catch (error) {
        console.error('ERROR WITH DELETE', error);
      }
    },
    [files, params.id],
  );

  const handleDownloadFile = useCallback(async (fileId, fileName) => {
    const response = await downloadFile(fileId, fileName);

    saveAs(response.data, fileName);
  }, []);

  return (
    <>
      {loading ? (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '70px',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      ) : (
        <>
          {!viewOnly && (
            <Grid
              container
              sx={{
                alignItems: 'center',
              }}
            >
              <Grid>
                <Typography
                  variant='h3'
                  sx={{
                    color: '#082253',
                    fontWeight: 600,
                  }}
                >
                  {getLocalString('labels', 'files')}
                </Typography>
              </Grid>
              <Grid>
                <StyledAddButton component='label' sx={{ padding: '6px 4px' }}>
                  <PlusIcon width='24' height='24' viewBox='0 0 24 24' />
                  <input
                    hidden
                    multiple
                    type='file'
                    onChange={event => handleFileUpload(event)}
                  />
                </StyledAddButton>
              </Grid>
              {loadingUpload && (
                <Grid
                  container
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '30px',
                    height: '30px',
                    marginLeft: '10px',
                  }}
                >
                  <CircularProgress size={20} color='secondary' />
                </Grid>
              )}
            </Grid>
          )}
          {files.length > 0 && (
            <Grid
              container
              direction={'column'}
              sx={{
                marginLeft: viewOnly ? '200px' : 0,
              }}
            >
              {files.map((file, index) => (
                <Grid
                  key={index}
                  sx={{
                    marginTop: '8px',
                    lineHeight: '20px',
                  }}
                >
                  <Grid container>
                    <Grid>
                      {!viewOnly ? (
                        <StyledIssueLink
                          sx={{ cursor: 'pointer' }}
                          onClick={() =>
                            handleDownloadFile(
                              file.attachmentUrl,
                              file.filename,
                            )
                          }
                          component={'span'}
                        >
                          <Typography
                            variant='h4'
                            sx={{
                              color: '#7174AC',
                            }}
                          >
                            {file.filename}
                          </Typography>
                        </StyledIssueLink>
                      ) : (
                        <Box
                          component={'span'}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <AttachIcon />
                          <Typography
                            variant='h4'
                            sx={{
                              color: '#7174AC',
                              marginLeft: '4px',
                            }}
                          >
                            {file.filename}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                    <Grid sx={{ display: 'flex', marginLeft: '13px' }}>
                      <StyledHoveredIcon
                        sx={{ padding: '0', marginLeft: '4px' }}
                        onClick={() =>
                          handleDeleteIssueFile(file.attachmentId, index)
                        }
                      >
                        <DeleteFileIcon />
                      </StyledHoveredIcon>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
    </>
  );
};

export default IssueFiles;
